import { useState } from 'react';

export interface BonLaboratoireFormData {
  medecin_id: string;
  date_prescription: string;
  type_analyse: string;
  description: string;
  observations_cliniques: string;
  urgence: boolean;
}

interface Medecin {
  id: string;
  nom: string;
  prenom: string;
  specialite: string;
}

interface BonLaboratoireFormProps {
  medecins: Medecin[];
  onSubmit: (data: BonLaboratoireFormData) => void;
  onCancel: () => void;
  loading: boolean;
}

const TYPES_ANALYSES = [
  'Hémogramme (NFS)',
  'Glycémie à jeun',
  'Bilan lipidique',
  'Bilan rénal',
  'Bilan hépatique',
  'Ionogramme sanguin',
  'Sérologie',
  'ECBU',
  'Goutte épaisse / Frottis',
  'Test de grossesse',
  'Groupage sanguin',
  'Bilan prénatal',
  'Bilan thyroïdien',
  'Bilan inflammatoire',
  'Autre',
];

export default function BonLaboratoireForm({ medecins, onSubmit, onCancel, loading }: BonLaboratoireFormProps) {
  const [form, setForm] = useState<BonLaboratoireFormData>({
    medecin_id: '',
    date_prescription: '',
    type_analyse: '',
    description: '',
    observations_cliniques: '',
    urgence: false,
  });

  const update = (field: keyof BonLaboratoireFormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.date_prescription || !form.medecin_id || !form.type_analyse) {
      alert('Veuillez remplir la date, le médecin et le type d\'analyse.');
      return;
    }
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Date de prescription *</label>
          <input
            type="date"
            required
            value={form.date_prescription}
            onChange={(e) => update('date_prescription', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Médecin prescripteur *</label>
          <select
            required
            value={form.medecin_id}
            onChange={(e) => update('medecin_id', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm appearance-none bg-white pr-8"
          >
            <option value="">Sélectionner un médecin...</option>
            {medecins.map((m) => (
              <option key={m.id} value={m.id}>
                Dr. {m.nom} {m.prenom} - {m.specialite}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Type d'analyse *</label>
          <select
            required
            value={form.type_analyse}
            onChange={(e) => update('type_analyse', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm appearance-none bg-white pr-8"
          >
            <option value="">Sélectionner...</option>
            {TYPES_ANALYSES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.urgence}
              onChange={(e) => update('urgence', e.target.checked)}
              className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-400"
            />
            <span className={`text-sm font-medium ${form.urgence ? 'text-red-600' : 'text-gray-700'}`}>
              <i className={`${form.urgence ? 'ri-alert-fill' : 'ri-alert-line'} mr-1 w-4 h-4 inline-flex items-center justify-center`}></i>
              Urgent
            </span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Description / Analyses demandées</label>
        <textarea
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          rows={2}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
          placeholder="Détail des analyses demandées..."
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Observations cliniques</label>
        <textarea
          value={form.observations_cliniques}
          onChange={(e) => update('observations_cliniques', e.target.value)}
          rows={2}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
          placeholder="Signes cliniques, constantes..."
        />
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium text-sm whitespace-nowrap cursor-pointer"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-5 py-2.5 bg-clinic-blue-dark text-white rounded-xl hover:bg-clinic-blue transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center gap-2"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Enregistrement...
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              Enregistrer le bon
            </>
          )}
        </button>
      </div>
    </form>
  );
}